import React, { useMemo } from 'react';
import styles from './ProposalRichText.module.css';
import { slugify } from '../../../parser/proposalToWebData';
import { sanitizeRichHtml } from '../../../utils/sanitizeRichHtml';
import { headingBackgroundOverrideCss, webTextStyleToInlineCss } from '../../../utils/proposalTextStyle';
import ProposalBoxAlign from '../ProposalBoxAlign/ProposalBoxAlign';
import PuckInlineRichText from './PuckInlineRichText';
import { useProposalProjectConfig } from '../../../contexts/ProposalProjectConfigContext';
import { themedTextStyle } from '../../../../shared/theme/themeTypography';

/**
 * Bloc Puck — Texte riche converti depuis Draft.js.
 * En édition Puck : Tiptap + bubble menu (couleurs, alignement, liens).
 */
const ProposalRichText = ({
  id,
  html = '',
  headingLevel = null,
  textStyle = null,
  drawMode = false,
  alignHorizontal = 'left',
  alignVertical = 'center',
  isVisible = true,
  /* Le style du proposal peut poser un fond sur le texte (configuration des
     titres/textes du document). Sans réglage ici, il n'y avait aucun moyen de
     le changer ni de l'enlever : ni le Bloc parent ni le surlignage du texte
     ne le portent. Vide = on garde celui du document. */
  backgroundColor = '',
  puck,
}) => {
  const isPuckEditing = !!puck?.isEditing;

  const htmlWithAnchors = useMemo(() => {
    if (!html || !isVisible) return '';
    if (typeof window === 'undefined' || !html) return html;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      if ([1, 2, 3, 4].includes(headingLevel)) {
        const first = doc.body.firstElementChild;
        if (first && first.tagName.toLowerCase() !== `h${headingLevel}`) {
          const heading = doc.createElement(`h${headingLevel}`);
          heading.innerHTML = first.innerHTML;
          first.replaceWith(heading);
        }
      }

      const h2s = Array.from(doc.querySelectorAll('h2'));
      const used = new Map();

      h2s.forEach((h2) => {
        if (h2.id) return;
        const base = slugify(h2.textContent || 'section');
        const count = used.get(base) || 0;
        used.set(base, count + 1);
        h2.id = count === 0 ? base : `${base}-${count + 1}`;
      });

      return doc.body.innerHTML;
    } catch {
      return html;
    }
  }, [html, headingLevel, isVisible]);

  const projectConfig = useProposalProjectConfig();

  /* 'transparent' efface le fond du document ; une couleur le remplace. */
  const effectiveTextStyle = useMemo(() => {
    const themed = themedTextStyle(textStyle, projectConfig);
    if (!backgroundColor) return themed;
    return {
      ...(themed || {}),
      backgroundColor: backgroundColor === 'transparent' ? null : backgroundColor,
    };
  }, [textStyle, projectConfig, backgroundColor]);

  /* La couleur choisie doit gagner sur `--proposal-hN-bg-color`, la variable
     qui pose le fond des titres depuis la configuration du proposal : peindre
     le conteneur ne suffit pas, le titre garderait son fond par-dessus. */
  const inlineStyle = {
    ...webTextStyleToInlineCss(effectiveTextStyle, { drawMode }),
    ...(headingBackgroundOverrideCss(backgroundColor) || {}),
  };

  const content = (
    <div
      id={id}
      className={`${styles.richText} ${effectiveTextStyle ? styles.boxStyled : ''} ${headingLevel ? styles.proposalTitle : ''}`.trim()}
      style={inlineStyle}
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(htmlWithAnchors) }}
    />
  );

  if (isPuckEditing) {
    return (
      <PuckInlineRichText
        id={id}
        puck={puck}
        html={html}
        contentProp="html"
        textStyle={effectiveTextStyle}
        drawMode={drawMode}
        alignHorizontal={alignHorizontal}
        alignVertical={alignVertical}
      />
    );
  }

  if (!html || !isVisible) return null;

  if (drawMode) {
    return content;
  }

  return (
    <ProposalBoxAlign horizontal={alignHorizontal} vertical={alignVertical} innerMode="full">
      {content}
    </ProposalBoxAlign>
  );
};

export default ProposalRichText;
